import React, { useState } from "react";
import { toast } from "sonner";
import { patchPipSettings } from "@client/api/pip";
import { t } from "@client/lib/dictionary";
import { showWriteResults } from "@client/lib/verifyToast";
import { useUserStore } from "@client/store/user";
import type { PipBatteryType, PipListItem, PipSettings } from "@client/types/pip";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";

interface Props {
  slug: string;
  record: PipListItem | undefined;
  settings: PipSettings | null | undefined;
}

const BATTERY_TYPE_LABELS: Record<PipBatteryType, string> = {
  AGM: "AGM",
  FLOODED: "Flooded (Wet)",
  USER: "User-defined",
  PYLONTECH: "Pylontech (BMS)",
  WECO: "WECO (BMS)",
  SOLTARO: "Soltaro (BMS)",
  LIB: "Lithium generic (BMS)",
  LIC: "Lithium custom (BMS)",
  CUSTOM: "Custom",
};

type DirtyFields = {
  batteryType?: PipBatteryType;
  shutdownV?: number;
  bulkV?: number;
  floatV?: number;
  rechargeV?: number;
  redischargeV?: number;
  gridRange?: "00" | "01";
};

/** Popisky polí pro ověřovací toasty (mapuje field klíč z WriteFieldResult). */
const FIELD_LABELS: Record<string, string> = {
  batteryType: t.pip.settings.batteryType,
  gridRange: t.pip.settings.gridRange,
  shutdownV: t.pip.settings.shutdownV,
  bulkV: t.pip.settings.bulkV,
  floatV: t.pip.settings.floatV,
  rechargeV: t.pip.settings.rechargeV,
  redischargeV: t.pip.settings.redischargeV,
};

export const PipSettingsForm: React.FC<Props> = ({ slug, record, settings }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";

  const [dirty, setDirty] = useState<DirtyFields>({});
  const [busy, setBusy] = useState(false);

  // Reset form po refresh settings (po každém write).
  // Pattern "store previous prop in state" doporučený v React docs místo useEffect+setState:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevSettings, setPrevSettings] = useState(settings);
  if (settings !== prevSettings) {
    setPrevSettings(settings);
    setDirty({});
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.pip.detail.settings}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.pip.controls.adminRequired}</CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.pip.detail.settings}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Čekám na settings z měniče…</CardContent>
      </Card>
    );
  }

  const setField = <K extends keyof DirtyFields>(key: K, value: DirtyFields[K]) => {
    setDirty((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (Object.keys(dirty).length === 0) {
      toast.info("Žádné změny");
      return;
    }
    setBusy(true);
    try {
      const res = await patchPipSettings(slug, dirty);
      showWriteResults(res.responseObject?.results ?? [], FIELD_LABELS);
      // dirty se vyčistí; reálné hodnoty dorazí přes `settings` WS event (žádné dosazení).
      setDirty({});
    } catch (err) {
      toast.error(String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  const valueOf = <K extends keyof DirtyFields>(key: K, fallback: DirtyFields[K]): DirtyFields[K] =>
    dirty[key] !== undefined ? dirty[key] : fallback;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.pip.detail.settings}</CardTitle>
        <p className="text-xs text-amber-600">⚠ {t.pip.settings.warning}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.batteryType}</span>
            <Select
              value={valueOf("batteryType", record?.batteryType ?? "USER") as string}
              onValueChange={(v) => setField("batteryType", v as PipBatteryType)}
              disabled={busy}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(BATTERY_TYPE_LABELS) as PipBatteryType[]).map((bt) => (
                  <SelectItem key={bt} value={bt}>
                    {BATTERY_TYPE_LABELS[bt]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.gridRange}</span>
            <Select
              value={valueOf("gridRange", settings.gridRange === "UPS" ? "01" : "00") as string}
              onValueChange={(v) => setField("gridRange", v as "00" | "01")}
              disabled={busy}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="00">{t.pip.settings.gridRangeOptions.APPLIANCE}</SelectItem>
                <SelectItem value="01">{t.pip.settings.gridRangeOptions.UPS}</SelectItem>
              </SelectContent>
            </Select>
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.shutdownV}</span>
            <Input
              type="number"
              step="0.1"
              defaultValue={settings.batteryUnderVoltageV}
              onChange={(e) => setField("shutdownV", Number(e.target.value))}
              disabled={busy}
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.bulkV}</span>
            <Input
              type="number"
              step="0.1"
              defaultValue={settings.batteryBulkVoltageV}
              onChange={(e) => setField("bulkV", Number(e.target.value))}
              disabled={busy}
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.floatV}</span>
            <Input
              type="number"
              step="0.1"
              defaultValue={settings.batteryFloatVoltageV}
              onChange={(e) => setField("floatV", Number(e.target.value))}
              disabled={busy}
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.rechargeV}</span>
            <Input
              type="number"
              step="0.1"
              defaultValue={settings.batteryRechargeVoltageV}
              onChange={(e) => setField("rechargeV", Number(e.target.value))}
              disabled={busy}
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">{t.pip.settings.redischargeV}</span>
            <Input
              type="number"
              step="0.1"
              defaultValue={settings.batteryRedischargeVoltageV}
              onChange={(e) => setField("redischargeV", Number(e.target.value))}
              disabled={busy}
            />
          </label>
        </div>

        <Button onClick={handleSave} disabled={busy || Object.keys(dirty).length === 0}>
          {busy ? t.app.loading : t.pip.settings.saveButton}
        </Button>
      </CardContent>
    </Card>
  );
};
